import { useCallback, useMemo, useRef } from 'react';
import {
  FieldErrors,
  FieldValues,
  Path,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import { LggButton } from 'src/components/general/button/lgg-button';
import {
  DrawerOption,
  OptionsBottomDrawer,
} from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { Icon } from 'src/components/general/icon';
import { LoadingSpinner } from 'src/components/general/inputs/shared';
import { useFormAutosave } from 'src/components/general/modals/inline-form/use-form-autosave';
import { LggModal } from 'src/components/general/modals/lgg-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { CustomTitleBottomDrawer } from 'src/components/pages/conversations/components/drawer/bottom-drawer';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible, VisibilityHandler } from 'src/hooks/use-visible';

const HeaderTitle = styled.p`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  letter-spacing: -0.28px;
  line-height: 18px;
  margin: 0;
`;

const Footer = styled(FlexRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.porcelain};
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 15px 20px;
`;

const ModalHeader = styled(FlexRow)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.alabaster};
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  border-radius: inherit;
  display: flex;
  justify-content: space-between;
  padding: 14px 20px;
`;

const MiddleContent = styled.div`
  padding: 10px 20px;
`;

const ModalCloseIcon = styled(Icon)`
  cursor: pointer;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const TopContent = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
`;

const BottomContent = styled(FlexRow)`
  padding: 20px;
  gap: 8px;
`;

const StyledFooterButton = styled(LggButton)`
  width: max-content;
  height: 34px;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 18px;
  padding: 8px 15px;
`;

const MobileTitleContainer = styled(FlexRow)`
  justify-content: space-between;
  width: 100%;
`;

const LoadingContainer = styled(FlexColumn)`
  align-items: center;
  height: 100%;
  justify-content: center;
  width: 100%;

  ${up('md')} {
    height: 580px;
  }
`;

const HeaderActionsContainer = styled(FlexRow)`
  gap: 15px;

  ${up('md')} {
    gap: 10px;
  }
`;

const MoreOptionsIcon = styled(Icon)`
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const LoadingContent = () => (
  <LoadingContainer>
    <LoadingSpinner size="40px" />
  </LoadingContainer>
);

type InlineFormModalProps<T extends FieldValues> = {
  title: string;
  visibilityHandler: VisibilityHandler;
  form: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
  children: React.ReactNode;
  topContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  onCancel?: () => void;
  isLoading?: boolean;
  isSubmitting?: boolean;
  disableSubmitButton?: boolean;
  testId?: string;
  submitButtonText?: string;
  onError?: (errors: FieldErrors<T>) => void;
  moreOptions?: DrawerOption[];
  width?: string;
  hideCancelButton?: boolean;
  // When enabled, the submit button is hidden and any valid field change is
  // auto-saved (debounced) via onAutosave instead of requiring a manual submit.
  autoSaveEnabled?: boolean;
  onAutosave?: (values: Partial<T>) => void | Promise<void>;
  // Fields auto-saved when focus leaves them instead of debounced while typing.
  autosaveOnBlurFields?: Path<T>[];
};

export const InlineFormModal = <T extends FieldValues>({
  title,
  visibilityHandler,
  form,
  onSubmit,
  children,
  topContent,
  bottomContent,
  onCancel,
  isLoading = false,
  isSubmitting = false,
  disableSubmitButton = false,
  testId,
  submitButtonText,
  onError,
  moreOptions,
  width = '580px',
  hideCancelButton = false,
  autoSaveEnabled = false,
  onAutosave,
  autosaveOnBlurFields,
}: InlineFormModalProps<T>) => {
  const { t } = useTranslation(['common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const moreOptionsVisibilityHandler = useVisible();

  const { onBlur: onAutosaveBlur, settle: settleAutosave } = useFormAutosave({
    form,
    enabled: autoSaveEnabled,
    onAutosave,
    saveOnBlurFields: autosaveOnBlurFields,
  });

  // Read the latest onCancel after awaiting: its dirty-state check must reflect the
  // re-render caused by the save marking its fields clean, not this render's closure.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Closing blurs the field being edited, which starts its save; wait for it so the
  // form isn't seen as dirty (prompting "unsaved changes") for edits being saved.
  const handleClose = useCallback(async () => {
    if (autoSaveEnabled) {
      await settleAutosave();
    }

    onCancelRef.current?.();
  }, [autoSaveEnabled, settleAutosave]);

  const footerContent = useMemo(() => {
    // In autosave (edit) mode there's nothing to submit/cancel explicitly — the
    // footer (Cancel included) is only shown in create mode.
    if (autoSaveEnabled) {
      return null;
    }

    return (
      <Footer>
        {!hideCancelButton && (
          <StyledFooterButton
            variant="tertiary"
            data-lgg-id={`${testId}-cancel-button`}
            onClick={onCancel}
          >
            {t('common:cancel')}
          </StyledFooterButton>
        )}
        <StyledFooterButton
          variant="primary"
          disabled={disableSubmitButton}
          loading={isSubmitting}
          onClick={isSubmitting ? undefined : form.handleSubmit(onSubmit, onError)}
          data-lgg-id={`${testId}-submit-button`}
        >
          {submitButtonText}
        </StyledFooterButton>
      </Footer>
    );
  }, [
    autoSaveEnabled,
    hideCancelButton,
    testId,
    onCancel,
    t,
    disableSubmitButton,
    isSubmitting,
    form,
    onSubmit,
    onError,
    submitButtonText,
  ]);

  const headerContent = useMemo(() => {
    return (
      <>
        <HeaderTitle data-lgg-id={`${testId}-title`}>{title}</HeaderTitle>
        <HeaderActionsContainer>
          {moreOptions && moreOptions.length > 0 && (
            <>
              {breakpointUpMd ? (
                <LggOptionsDropdownButtonWithCustomTrigger
                  options={moreOptions}
                  customTrigger={
                    <MoreOptionsIcon
                      type="moreOptions"
                      lggTestId={`${testId}-more-options-trigger`}
                    />
                  }
                  visibilityHandler={moreOptionsVisibilityHandler}
                  overlayTestId={`${testId}-more-options-container`}
                  customDropdownProps={{
                    placement: 'bottomRight',
                  }}
                />
              ) : (
                <>
                  <MoreOptionsIcon
                    type="moreOptions"
                    lggTestId={`${testId}-more-options-trigger`}
                    onClick={moreOptionsVisibilityHandler.show}
                  />
                  <OptionsBottomDrawer
                    visible={moreOptionsVisibilityHandler.visible}
                    onClose={moreOptionsVisibilityHandler.close}
                    title={t('common:moreOptions')}
                    options={moreOptions}
                  />
                </>
              )}
            </>
          )}
          <ModalCloseIcon
            type="close"
            data-lgg-id={`${testId}-close-icon`}
            onClick={() => void handleClose()}
          />
        </HeaderActionsContainer>
      </>
    );
  }, [
    title,
    moreOptions,
    breakpointUpMd,
    moreOptionsVisibilityHandler,
    testId,
    t,
    handleClose,
  ]);

  if (breakpointUpMd) {
    return (
      <LggModal
        width={width}
        onClose={() => void handleClose()}
        visible={visibilityHandler.visible}
        closeIcon={<></>}
        closable={false}
        data-lgg-id={testId}
        maskClosable={false}
      >
        {isLoading ? (
          <LoadingContent />
        ) : (
          <>
            <ModalHeader>{headerContent}</ModalHeader>
            {topContent && <TopContent onBlur={onAutosaveBlur}>{topContent}</TopContent>}
            <MiddleContent onBlur={onAutosaveBlur}>{children}</MiddleContent>
            {bottomContent && (
              <BottomContent onBlur={onAutosaveBlur}>{bottomContent}</BottomContent>
            )}
            {footerContent}
          </>
        )}
      </LggModal>
    );
  }

  return (
    <CustomTitleBottomDrawer
      title={
        isLoading ? <></> : <MobileTitleContainer>{headerContent}</MobileTitleContainer>
      }
      onClose={visibilityHandler.close}
      visible={visibilityHandler.visible}
      fullHeight
      push={false}
      footer={isLoading ? <></> : footerContent}
      headerVariant="compact"
    >
      {isLoading ? (
        <LoadingContent />
      ) : (
        <>
          {topContent && <TopContent onBlur={onAutosaveBlur}>{topContent}</TopContent>}
          <MiddleContent onBlur={onAutosaveBlur}>{children}</MiddleContent>
          {bottomContent && (
            <BottomContent onBlur={onAutosaveBlur}>{bottomContent}</BottomContent>
          )}
        </>
      )}
    </CustomTitleBottomDrawer>
  );
};
